import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Form, Formik, FormikHelpers } from "formik";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Col, Row } from "react-bootstrap";
import { createServiceFormValidationSchema, SetFieldValueType } from "./constants";
import { ApolloError, useReactiveVar } from "@apollo/client";
import InputWithCounter from "./inputs/TextareaInput";
import TextInputWithCounter from "./inputs/TextInputWithCounter";
import CheckBox from "./inputs/CheckBox";
import { DurationInput, Spinner, WithModulesAccess, SpinnerLoader } from "@services/ui-components";
import PriceInput from "./inputs/PriceInput";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ErrorService from "../../../services/ErrorService";
import { ServiceModalWrapper } from "@me-pos/service-modal";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import {
    useCreateServCategoryMutation,
    useGetCategoryServQuery,
    useGetOneServiceQuery,
    useRestoreServiceMutation,
    useUpdateServiceMutation,
} from "@me-team/host/main/services/graphql/services.hooks";
import {
    CompanyAccess,
    ServiceCategoryNewInput,
    ServiceCategoryNewProxy,
    ServiceInput,
} from "@me-team/host/main/graphql/types";
import CategoryService from "./utils/CategoryService";
import RequestInputService from "./utils/RequestInputService";
import { CategoryInterface } from "./type";
import categoryService from "./utils/CategoryService";
import { normalizeCategoryData } from "./utils/normalizeCategoryData";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { AllModulesTagName } from "../../../utils/enums";
import { useServiceChangesTracker } from "../ServiceProviderSettings/utils/hooks/useServiceChangesTracker";
import UpdateRecoveryServiceFormEffects from "./UpdateRecoveryServiceFormEffects";
import { ServiceProviderSettingsNavigationState } from "../../../interfaces/interfaces";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

export interface InitValues {
    name: string;
    category: number;
    price: number;
    duration: string;
    onlineBooking?: boolean;
    description?: string;
    image?: File | null;
    isUpdateImage?: boolean;
}

interface UpdateRecoveryServiceFormProps {
    canEditService?: boolean;
}

const UpdateRecoveryServiceForm: React.FC<UpdateRecoveryServiceFormProps> = ({ canEditService = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, branchId } = useParams();
    const buttonCategoryRef = useRef<HTMLButtonElement>(null);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const isEdit = useMatch(`/services/service/${currentCompanyId}/service-list/${id}/edit/${branchId}`);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [isServiceModalOpen, setIsServiceModalOpen] = useState<boolean>(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoryList, setCategoryList] = useState<CategoryInterface[]>(null);
    const [categoryInput, setCategoryInput] = useState(null);
    const [pickedCategoryId, setPickedCategoryId] = useState<number>(null);
    const [isArchiveCategory, setIsArchiveCategory] = useState<boolean>(false);
    const canCreateServiceCategory = useRoleAccess(CompanyAccess.ServiceCategoryCreate);

    const { captureOldValues, calculateChangesWithNewValues } = useServiceChangesTracker();

    const { data: oneServiceData, loading } = useGetOneServiceQuery({
        variables: {
            id: +id,
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const service = oneServiceData?.user?.company?.services?.services[0];
    const initServiceCategory: ServiceCategoryNewProxy = service?.serviceCategoryNew;

    const startValueCategory = service?.serviceCategoryNew?.parentCategory?.id
        ? service?.serviceCategoryNew?.parentCategory?.id
        : service?.serviceCategoryNew?.id;

    const startValueSubCategory = service?.serviceCategoryNew?.parentCategory?.id
        ? service?.serviceCategoryNew?.id
        : null;

    const categoryName = service?.serviceCategoryNew?.parentCategory?.name
        ? service?.serviceCategoryNew?.parentCategory?.name
        : service?.serviceCategoryNew?.name;

    const SubCategoryName = service?.serviceCategoryNew?.parentCategory?.name
        ? service?.serviceCategoryNew?.name
        : null;

    const [categoryText, setCategoryText] = useState<[string | null, string | null]>([
        categoryName ?? null,
        SubCategoryName ?? null,
    ]);

    const [updateService] = useUpdateServiceMutation();
    const [restoreService] = useRestoreServiceMutation();
    const [createCategoryService] = useCreateServCategoryMutation();

    const handleSubmitCreate = async (input: ServiceCategoryNewInput) => {
        if (!canCreateServiceCategory) return;

        try {
            currentCompanyId &&
                (await createCategoryService({
                    variables: {
                        input,
                        branchId: +branchId,
                        companyId: +currentCompanyId,
                    },
                    context: {
                        errorType: "local",
                    },
                    onCompleted: (data) => {
                        data && setCurrentCategory(data.createServiceCategoryNew);
                    },
                    onError: (error: ApolloError) => {
                        setIsErrorModalOpen(ErrorService.errorHandling(error));
                    },
                }));
        } catch (error) {
            console.error(error);
        }
    };

    const {
        data: serviceCategoriesData,
        refetch: refetchCategories,
        loading: loadingServiceCategories,
    } = useGetCategoryServQuery({
        variables: {
            branchId: Number(branchId),
            id: null,
            categoryName: null,
            serviceDeleted: true,
            page: 1,
            itemsPerPage: 1000,
        },
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setCategoryList(
                categoryService.categoryModuleCreator(normalizedItems, startValueCategory, startValueSubCategory)
            );
            setCategoryInput(
                RequestInputService.createServiceCategoryInput(
                    startValueCategory,
                    startValueSubCategory,
                    normalizedItems
                )
            );
        },
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const categoryListData: ServiceCategoryNewProxy[] = normalizeCategoryData(serviceCategoriesData);

    const handlePhoto = (setFieldValue: SetFieldValueType) => {
        setFieldValue("isUpdateImage", true);
    };

    const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement>) => {
        event.preventDefault();
        if (buttonCategoryRef.current) {
            buttonCategoryRef.current.focus();
        }
    };

    const handleCategoryNameChange = (categoryName?: string) => {
        setCategoryText([categoryName ?? null, null]);
    };

    const handleSubCategoryNameChange = (subCategoryName?: string, categoryName?: string) => {
        setCategoryText([categoryName ?? null, subCategoryName ?? null]);
    };

    const checkIsArchiveCategory = (
        selectedCategory: ServiceCategoryNewProxy,
        initialCategory: ServiceCategoryNewProxy
    ) => {
        const categoryToCheck = selectedCategory ?? initialCategory;
        const isArchived = Boolean(categoryToCheck?.deleted);
        setIsArchiveCategory(isArchived);
    };

    useEffect(() => {
        if (service) {
            captureOldValues({
                duration: service.duration,
                price: service.price,
                online: service.onlineRegistration,
            });
        }
    }, [service, captureOldValues]);

    const initialValues: InitValues = {
        name: service?.name ? service?.name : "",
        category: service?.serviceCategoryNew?.id && service?.serviceCategoryNew?.id,
        duration: service?.duration ? service?.duration : "",
        price: service?.price ? service?.price : 0,
        onlineBooking: service?.onlineRegistration ? service?.onlineRegistration : false,
        description: service?.description ? service?.description : "",
    };

    const handleUpdateSubmit = async (values: InitValues, actions: FormikHelpers<InitValues>) => {
        if (!canEditService) {
            actions.setSubmitting(false);
            return;
        }

        if (isArchiveCategory) {
            actions.setErrors({
                category: t("Select a non-archived category to restore the service"),
            });
            actions.setSubmitting(false);
            return;
        }

        const categoryIdValue = values?.category;
        const input: ServiceInput = {
            company: +currentCompanyId,
            name: values?.name,
            category: categoryIdValue && categoryIdValue,
            duration: values?.duration,
            price: values?.price,
            onlineBooking: values?.onlineBooking,
            description: values?.description,
            isUpdateImage: true,
            image: values?.image ? values?.image : null,
        };

        const mutateService = isEdit ? updateService : restoreService;

        currentCompanyId &&
            (await mutateService({
                variables: { id: +id, input, companyId: +currentCompanyId, branchId: +branchId },
                context: {
                    errorType: "local",
                },
                onCompleted: (data) => {
                    const updatedService = "updateService" in data ? data.updateService : data.restoreService;
                    const newServiceValues = {
                        duration: updatedService.duration,
                        price: updatedService.price,
                        online: updatedService.onlineRegistration,
                    };

                    const changes = calculateChangesWithNewValues(newServiceValues);
                    const hasChanges = Object.keys(changes).length > 0;
                    const performersCount = service?.employeeServiceRelations?.length ?? 0;
                    const shouldOpenProviderSettingsModal = hasChanges && performersCount > 0;

                    actions.setSubmitting(false);

                    const navigationState: ServiceProviderSettingsNavigationState = {
                        text: isEdit ? t("Saved") : t("Restored"),
                        toast: true,
                        openServiceProviderSettingsModal: shouldOpenProviderSettingsModal,
                        serviceProviderSettingsPayload: shouldOpenProviderSettingsModal
                            ? {
                                  serviceId: Number(id),
                                  serviceName: updatedService?.name ?? values.name ?? "",
                                  changes,
                              }
                            : undefined,
                    };
                    navigate(`/services/service/${currentCompanyId}/service-list/${branchId}`, {
                        state: navigationState,
                    });
                },
                onError: (error: ApolloError) => {
                    const graphQLErrors = error?.graphQLErrors[0]?.extensions?.category;
                    if (graphQLErrors === "service.exists") {
                        actions.setErrors({
                            name: t("A service with this name and category already exists."),
                        });
                    }
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            }));
    };

    if (loading || loadingServiceCategories) return <Spinner />;

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={createServiceFormValidationSchema(t)}
                onSubmit={handleUpdateSubmit}
            >
                {({ isSubmitting, setFieldValue, touched, errors }) => {
                    return (
                        <>
                            <UpdateRecoveryServiceFormEffects
                                pickedCategoryId={pickedCategoryId}
                                initServiceCategory={initServiceCategory}
                                checkIsArchiveCategory={checkIsArchiveCategory}
                            />
                            <Form>
                                <Row className="flex-column flex-lg-row mb-md-3 mb-lg-5">
                                    <Col lg={9} className="pe-lg-5">
                                        <h4 className="mb-3">{t("Information")}</h4>
                                        <Row className="mb-3 mb-sm-4">
                                            <TextInputWithCounter
                                                id="updateService-name-field"
                                                label={t("Service name")}
                                                name="name"
                                                placeholder={t("Enter a service name")}
                                                classes="text-truncate pe-5"
                                                disabled={!canEditService}
                                            />
                                        </Row>
                                        <Row className="mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="updateService-category-field"
                                                    className="text-dark fs-7 fw-bold py-1 required-label"
                                                    onClick={handleLabelClick}
                                                >
                                                    {t("category")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <div>
                                                    <span
                                                        className={`${categoryText[0] ? "me-4" : "d-none"}  mb-2 mb-lg-0 ${isArchiveCategory ? "text-grey" : "text-dark"}`}
                                                    >
                                                        {categoryText[0]}{" "}
                                                        {categoryText[1] && (
                                                            <>
                                                                <i className="bi bi-arrow-right"></i> {categoryText[1]}
                                                            </>
                                                        )}{" "}
                                                        {isArchiveCategory && `(${t("Archive")})`}
                                                    </span>
                                                    <Button
                                                        ref={buttonCategoryRef}
                                                        variant={`${touched.category && errors.category ? "outline-danger" : "outline-primary"}`}
                                                        id="updateService-category-field"
                                                        className="w-100 fw-normal py-1"
                                                        style={{ maxWidth: "149px" }}
                                                        onClick={() => {
                                                            setIsServiceModalOpen(true);
                                                        }}
                                                        disabled={!canEditService}
                                                    >
                                                        {categoryText[0] ? t("Change Category") : t("Select Category")}
                                                    </Button>
                                                    <ErrorMessage name="category">
                                                        {(msg) => (
                                                            <div className="text-danger fs-7 mt-1">
                                                                {isArchiveCategory && (
                                                                    <i className="bi bi-exclamation-circle text-danger me-1 fw-bold"></i>
                                                                )}
                                                                {msg}
                                                            </div>
                                                        )}
                                                    </ErrorMessage>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row className="mb-sm-4">
                                            <InputWithCounter
                                                id="updateService-description-field"
                                                label={t("description")}
                                                name="description"
                                                placeholder={t("Add a short description of the service")}
                                                disabled={!canEditService}
                                            />
                                        </Row>
                                        <Row className="mb-sm-4 durationWrapper">
                                            <DurationInput
                                                id="updateService-duration-field"
                                                name={"duration"}
                                                label={t("Duration")}
                                                prevValue={service?.duration && service?.duration.slice(0, 5)}
                                                isDisabled={!canEditService}
                                            />
                                        </Row>
                                        <Row className="mb-sm-4">
                                            <PriceInput
                                                id="updateService-price-field"
                                                name="price"
                                                placeholder="0.00"
                                                initialValue={service?.price && service?.price}
                                                disabled={!canEditService}
                                            />
                                        </Row>
                                        <WithModulesAccess modules={[AllModulesTagName.BOOKING]}>
                                            <Row className="flex align-items-center pt-3">
                                                <CheckBox
                                                    id="updateService-onlineBooking-field"
                                                    name="onlineBooking"
                                                    label={t("Online Registration")}
                                                    value={service?.onlineRegistration && service?.onlineRegistration}
                                                    disabled={!canEditService}
                                                />
                                            </Row>
                                        </WithModulesAccess>
                                    </Col>
                                    <Col lg={4} xxl={3} className="mt-4">
                                        <div className="d-flex mb-3 w-100 flex-column justify-content-lg-end">
                                            <h4 className="mb-3 w-100">{t("Services Photo")}</h4>
                                            <ImageCrop
                                                previewPhoto={service?.image}
                                                setFieldValue={setFieldValue}
                                                aspectProp={2.1 / 1}
                                                fieldSetter={() => handlePhoto(setFieldValue)}
                                                fieldName="image"
                                                dismissImage={() => setFieldValue("isUpdateImage", true)}
                                                modalTitle={t("Services Photo")}
                                                notBGinfo={true}
                                                disabled={!canEditService}
                                            />
                                            <ErrorMessage name="image" component="div" className="invalid-feedback" />
                                        </div>
                                    </Col>
                                </Row>
                                <div className="d-sm-flex">
                                    <Button
                                        variant="outline-primary"
                                        className="custom-btn mb-3 mb-sm-0 me-3"
                                        onClick={() =>
                                            navigate(`/services/service/${currentCompanyId}/service-list/${branchId}`)
                                        }
                                    >
                                        {t("cancel")}
                                    </Button>
                                    {isEdit ? (
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="custom-btn"
                                            disabled={isSubmitting || !canEditService}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader variant="light" loadingText={t("Editing...")} />
                                            ) : (
                                                t("edit")
                                            )}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="custom-btn"
                                            disabled={isSubmitting || !canEditService}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader variant="light" loadingText={t("Restoring...")} />
                                            ) : (
                                                t("Restore")
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </Form>
                            {isServiceModalOpen && serviceCategoriesData && !loadingServiceCategories ? (
                                <ServiceModalWrapper
                                    companyId={Number(currentCompanyId)}
                                    branchId={Number(branchId)}
                                    i18n={i18next}
                                    selectedCategoryId={startValueCategory}
                                    selectedSubcategoryId={startValueSubCategory}
                                    apolloUrl={process.env.REACT_APP_API_URL}
                                    isOpen={isServiceModalOpen}
                                    onClose={() => {
                                        setIsServiceModalOpen(!isServiceModalOpen);
                                    }}
                                    onCategorySelect={(selectedCategory) => {
                                        handleCategoryNameChange(selectedCategory?.name);
                                        setPickedCategoryId(selectedCategory?.id);
                                        checkIsArchiveCategory(selectedCategory, initServiceCategory);
                                        setIsServiceModalOpen(!isServiceModalOpen);
                                    }}
                                    onSubcategorySelect={(selectedSubcategory) => {
                                        handleSubCategoryNameChange(
                                            selectedSubcategory?.name,
                                            selectedSubcategory?.parentCategory?.name
                                        );
                                        setPickedCategoryId(selectedSubcategory?.id);
                                        checkIsArchiveCategory(selectedSubcategory, initServiceCategory);
                                        setIsServiceModalOpen(!isServiceModalOpen);
                                    }}
                                    categoryList={categoryList}
                                    categoryInput={categoryInput}
                                    categoryListData={categoryListData}
                                    refetchCategories={refetchCategories}
                                    onSubmitCreate={handleSubmitCreate}
                                    canCreateCategory={canCreateServiceCategory}
                                    currentCategory={currentCategory}
                                    categoryService={CategoryService}
                                    requestInputService={RequestInputService}
                                    normalizeCategoryData={normalizeCategoryData}
                                />
                            ) : null}
                        </>
                    );
                }}
            </Formik>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default UpdateRecoveryServiceForm;
